
import Vue from "vue";
import organizationApi from "@/services/api.organizationSettings";
import { Getter, namespace } from "vuex-class";
import { UserPermissions } from "@/store/types";
import Component from "vue-class-component";
import SearchDropDown from "@/components/form/SearchDropDown.vue";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import { CustomFieldDto, CustomFieldTableTypes, CustomFieldTypes } from "@/store/custom-fields/types";
import { NotificationSubjectLineConstants } from "@/types/organization-settings/NotificationSubjectLineSettings";

const CustomFields = namespace("customFields");

@Component({
	components: {
		"search-dropdown": SearchDropDown,
	},
})
export default class NotificationSubjectLineForm extends Vue {
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	@CustomFields.State private areaCustomFields: CustomFieldDto[];
	@CustomFields.Action private retrieveCustomFields: ({ tableType, live, skipCancel, excludeTypeIds }) => Promise<void>;

	private isUpdating: boolean = false;
	private subjectNotificationLine: string = "";
	private newSubjectNotificationLine: string = "";

	private customFields: RichTextEditorPlaceholder[] = [];
	private placeholderFields: RichTextEditorPlaceholder[] = [
		{
			id: "areaName",
			label: "Affected Area",
		},
		{
			id: "areaAddress",
			label: "Area Address",
		},
		{
			id: "areaReferenceId",
			label: "Area Unique Identifier",
		},
		{
			id: "eventCreatedDate",
			label: "Event Started",
		},
		{
			id: "eventOutcome",
			label: "Event Outcome",
		},
		{
			id: "eventOutcomeNote",
			label: "Event Outcome Note",
		},
		{
			id: "note",
			label: "Note",
		},
		{
			id: "senderName",
			label: "Sender Name",
		},
		{
			id: "eventId",
			label: "Event ID",
		},
	];

	private async beforeMount(): Promise<void> {
		try {
			this.subjectNotificationLine = (await organizationApi.retrieveNotificationSubjectLine(null)).subjectLine;
		} catch {
			this.subjectNotificationLine = "";
		}

		if (this.hasRequiredCustomFieldAccess) {
			await this.retrieveCustomFields({
				tableType: CustomFieldTableTypes.Area,
				live: true,
				skipCancel: false,
				excludeTypeIds: [CustomFieldTypes.Icon],
			});
		}

		this.newSubjectNotificationLine = this.subjectNotificationLine;
	}

	private haveSettingsChanged(): boolean {
		return this.isUpdating || this.newSubjectNotificationLine === this.subjectNotificationLine;
	}

	private async confirmUpdateNotificationSubjectLineSettings(): Promise<void> {
		this.isUpdating = true;
		try {
			const response = await organizationApi.createOrUpdateNotificationSubjectLine({
				subjectLine: this.newSubjectNotificationLine,
			});
			this.subjectNotificationLine = response.subjectLine;
			this.newSubjectNotificationLine = response.subjectLine;

			this.$notify({
				type: "success",
				title: "Success",
				text: "Notification Subject Line updated",
			});
			this.isUpdating = false;
		} catch {
			this.isUpdating = false;
		}
	}

	private resetChanges(): void {
		this.newSubjectNotificationLine = this.subjectNotificationLine;
	}

	private addPlaceholder(placeholder: RichTextEditorPlaceholder): void {
		this.newSubjectNotificationLine += `{{${placeholder.id}}}`;
	}

	private addCustomField(placeholder: RichTextEditorPlaceholder): void {
		this.newSubjectNotificationLine += `{{${placeholder.label}.AreaCustomField-${placeholder.id}}}`;
	}

	private get hasRequiredCustomFieldAccess(): boolean {
		return (
			!!this.getFeature(["Suite"]) &&
			!!this.getFeature(["CustomFields", "NotificationTemplates"]) &&
			this.permissions.canViewCustomFields
		);
	}

	private get getAreaCustomFieldPlaceholders(): RichTextEditorPlaceholder[] {
		this.customFields = [];
		if (!this.hasRequiredCustomFieldAccess) {
			return this.customFields;
		}

		if (Array.isArray(this.areaCustomFields)) {
			this.areaCustomFields.forEach((cf) => {
				this.customFields.push({
					id: cf.id,
					label: cf.title,
				});
			});
		}
		return this.customFields;
	}

	private get maxSubjectLength(): number {
		return NotificationSubjectLineConstants.MaximumCharacterLength;
	}
}
